import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackofficeLayout } from '../BackofficeLayout';
import {
  ActionDrawerItems,
  Button,
  Drawer,
  DrawerList,
  Icon,
  Modal,
  PageHeading,
  Pills,
  Search,
  Section,
  Select,
  Table,
  Text,
} from 'barte-design-system';
import type { ColumnDefinition } from 'barte-design-system';
import {
  CaretDown,
  ClockCounterClockwise,
  PencilSimple,
  Plus,
  Storefront,
  Trash,
  UploadSimple,
} from '@phosphor-icons/react';
import {
  ALL_SELLERS,
  INITIAL_PLANOS,
  INITIAL_SELLERS_BY_PLANO,
  COMPANY_MOCK,
  type PlanoTaxa,
} from '../CadastroPlanoTaxas/sharedData';
import { GroupSelect } from './GroupSelect';
import './DetalhePlanoTaxas.css';

// ─── Types ────────────────────────────────────────────────────────────────────

type TipoPagamentoTab = 'credito-online' | 'credito-pos';

interface RatesByGroup {
  groupId: string;
  groupLabel: string;
  mccDefault: string;
  mccs: string[];
  isDefault?: boolean;
  pix: { tempo: string; tipo: 'Fixo' | '%'; taxa: string; limite: string };
  boleto: { tempo: string; tipo: 'Fixo' | '%'; taxa: string; limite: string };
  debito: { tempo: string; tipo: 'Fixo' | '%'; taxa: string };
  credito: {
    online: number[][]; // 4 rows × 5 cols
    pos: number[][];
  };
}

const BANDEIRAS = ['Mastercard', 'Visa', 'Elo', 'Amex', 'Hiper'];
const PARCELAS = ['1x', '2 a 6x', '7 a 12x', '13 a 24x'];

// Taxas de método (PIX/Boleto/Débito) são por PLANO, não mudam com o grupo de MCC.
const PLANO_TAXAS_METODO = {
  pix:    { tempo: 'D + 1', tipo: 'Fixo' as 'Fixo' | '%', taxa: 'R$ 0,70', limite: 'R$ 10.000,00' },
  boleto: { tempo: 'D + 2', tipo: 'Fixo' as 'Fixo' | '%', taxa: 'R$ 1,50', limite: 'R$ 20.000,00' },
  debito: { tempo: 'D + 1', tipo: '%'    as 'Fixo' | '%', taxa: '1,05%',   limite: '—' },
};
const REPASSE_DAYS = ['D+1', 'D+2', 'D+3', 'D+10', 'D+14', 'D+30'];
// Custo de antecipação por dia de repasse (D+30 = base, sem antecipação)
const ANTECIPACAO_DELTA: Record<string, number> = {
  'D+1':  0.50,
  'D+2':  0.42,
  'D+3':  0.35,
  'D+10': 0.22,
  'D+14': 0.15,
  'D+30': 0.00,
};

// Mock determinístico — diferentes valores por grupo pra UX feliz
const RATES_MOCK: RatesByGroup[] = [
  {
    groupId: 'grp-1',
    groupLabel: 'Grupo 1 — Saúde',
    mccDefault: '8011',
    mccs: ['8011', '8021', '8062'],
    isDefault: true,
    pix: { tempo: 'D + 1', tipo: 'Fixo', taxa: 'R$ 0,70', limite: 'R$ 10.000,00' },
    boleto: { tempo: 'D + 2', tipo: 'Fixo', taxa: 'R$ 1,50', limite: 'R$ 20.000,00' },
    debito: { tempo: 'D + 1', tipo: '%', taxa: '1,05%' },
    credito: {
      online: [
        [1.05, 1.37, 3.04, 0.83, 2.32],
        [0.34, 2.23, 3.51, 1.11, 3.57],
        [0.46, 2.96, 3.73, 1.11, 3.97],
        [3.45, 3.45, 3.45, 3.45, 3.45],
      ],
      pos: [
        [1.20, 1.55, 3.20, 0.95, 2.50],
        [0.50, 2.40, 3.70, 1.30, 3.75],
        [0.65, 3.15, 3.95, 1.30, 4.15],
        [3.65, 3.65, 3.65, 3.65, 3.65],
      ],
    },
  },
  {
    groupId: 'grp-2',
    groupLabel: 'Grupo 2 — Tecnologia',
    mccDefault: '7372',
    mccs: ['7372', '5732'],
    pix: { tempo: 'D + 1', tipo: 'Fixo', taxa: 'R$ 0,50', limite: 'R$ 25.000,00' },
    boleto: { tempo: 'D + 1', tipo: '%', taxa: '0,80%', limite: 'R$ 15.000,00' },
    debito: { tempo: 'D + 1', tipo: '%', taxa: '0,95%' },
    credito: {
      online: [
        [0.95, 1.25, 2.85, 0.75, 2.15],
        [0.28, 2.05, 3.30, 1.00, 3.40],
        [0.40, 2.75, 3.55, 1.05, 3.80],
        [3.20, 3.20, 3.20, 3.20, 3.20],
      ],
      pos: [
        [1.10, 1.40, 3.05, 0.90, 2.35],
        [0.45, 2.25, 3.55, 1.20, 3.60],
        [0.60, 2.95, 3.80, 1.25, 4.00],
        [3.45, 3.45, 3.45, 3.45, 3.45],
      ],
    },
  },
  {
    groupId: 'grp-3',
    groupLabel: 'Grupo 3 — Varejo',
    mccDefault: '5411',
    mccs: ['5411', '5912', '5651', '5944', '5999'],
    pix: { tempo: 'D + 0', tipo: 'Fixo', taxa: 'R$ 0,40', limite: 'R$ 50.000,00' },
    boleto: { tempo: 'D + 2', tipo: 'Fixo', taxa: 'R$ 1,80', limite: 'R$ 50.000,00' },
    debito: { tempo: 'D + 1', tipo: '%', taxa: '1,20%' },
    credito: {
      online: [
        [1.15, 1.45, 3.15, 0.90, 2.40],
        [0.40, 2.30, 3.60, 1.20, 3.65],
        [0.55, 3.05, 3.80, 1.20, 4.05],
        [3.55, 3.55, 3.55, 3.55, 3.55],
      ],
      pos: [
        [1.30, 1.65, 3.35, 1.05, 2.60],
        [0.55, 2.50, 3.85, 1.40, 3.85],
        [0.70, 3.25, 4.05, 1.40, 4.25],
        [3.75, 3.75, 3.75, 3.75, 3.75],
      ],
    },
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

export function DetalhePlanoTaxas() {
  const navigate = useNavigate();
  const { planoId } = useParams<{ planoId: string }>();

  // Resolve plano da lista mock
  const plano: PlanoTaxa | undefined = useMemo(() => {
    const id = parseInt(planoId ?? '', 10);
    return INITIAL_PLANOS.find((p) => p.id === id);
  }, [planoId]);

  const planoNome = plano?.nome ?? 'Plano criado';

  // Filtros
  const [grupoId, setGrupoId] = useState<string>(RATES_MOCK[0].groupId);
  const [tipoPagamento] = useState<TipoPagamentoTab>('credito-online');
  const [defaultGroupId, setDefaultGroupId] = useState<string>(
    RATES_MOCK.find((g) => g.isDefault)?.groupId ?? RATES_MOCK[0].groupId,
  );

  // Drawer de taxas detalhadas por célula clicada
  const [cellDrawerOpen, setCellDrawerOpen] = useState(false);
  const [drawerParcelaIdx, setDrawerParcelaIdx] = useState(0);
  const [drawerBandeiraIdx, setDrawerBandeiraIdx] = useState(0);

  // Modais
  const [sellersModalOpen, setSellersModalOpen] = useState(false);
  const [historicoModalOpen, setHistoricoModalOpen] = useState(false);
  const [confirmDefaultGroupId, setConfirmDefaultGroupId] = useState<string | null>(null);

  function labelGrupo(groupId: string): string {
    const idx = RATES_MOCK.findIndex((g) => g.groupId === groupId);
    return idx >= 0 ? `MCCs - Grupo ${idx + 1}` : groupId;
  }

  // ── Modo de edição ──────────────────────────────────────────────────────────
  type EditState = {
    miniCards: { cdi: string; gateway: string; antifraude: string; repasse: string; tds: string };
    pix:    { tempo: string; tipo: string; taxa: string; limite: string };
    boleto: { tempo: string; tipo: string; taxa: string; limite: string };
    debito: { tempo: string; tipo: string; taxa: string; limite: string };
    matrix: string[][]; // 4 × 5 — espelha o creditoMatrix em string
  };
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState<EditState | null>(null);

  function snapshotCurrent(): EditState {
    return {
      miniCards: { cdi: '130%', gateway: 'R$ 0,29', antifraude: 'R$ 0,29', repasse: 'D+1', tds: '0,59%' },
      pix:    { ...PLANO_TAXAS_METODO.pix,    tipo: PLANO_TAXAS_METODO.pix.tipo === '%' ? 'Porcentagem' : 'Fixo' },
      boleto: { ...PLANO_TAXAS_METODO.boleto, tipo: PLANO_TAXAS_METODO.boleto.tipo === '%' ? 'Porcentagem' : 'Fixo' },
      debito: { ...PLANO_TAXAS_METODO.debito, tipo: PLANO_TAXAS_METODO.debito.tipo === '%' ? 'Porcentagem' : 'Fixo' },
      matrix: (tipoPagamento === 'credito-online' ? grupoAtual.credito.online : grupoAtual.credito.pos)
        .map((row) => row.map((v) => `${v.toFixed(2).replace('.', ',')}%`)),
    };
  }

  function startEdit() {
    setEditValues(snapshotCurrent());
    setEditMode(true);
  }
  function cancelEdit() {
    setEditMode(false);
    setEditValues(null);
  }
  function saveEdit() {
    setEditMode(false);
    setEditValues(null);
  }

  // ── Dropdown "Editar" (Individual / Em massa) ──────────────────────────────
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const headerWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editDropdownOpen) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (headerWrapRef.current && !headerWrapRef.current.contains(target)) {
        setEditDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [editDropdownOpen]);
  const [sellersInPlano, setSellersInPlano] = useState<string[]>(
    () => (plano ? INITIAL_SELLERS_BY_PLANO[plano.id] ?? [] : []),
  );
  const [sellerQuery, setSellerQuery] = useState('');
  const [sellerSearchExpanded, setSellerSearchExpanded] = useState(false);
  const [sellerToAdd, setSellerToAdd] = useState('');

  const sellersCount = sellersInPlano.length;

  const modalAvailableSellers = ALL_SELLERS.filter((s) => !sellersInPlano.includes(s.id));
  const modalFilteredSellers = useMemo(() => {
    const q = sellerQuery.trim().toLowerCase();
    if (!q) return sellersInPlano;
    return sellersInPlano.filter((id) => {
      const s = ALL_SELLERS.find((x) => x.id === id);
      if (!s) return false;
      return (
        s.id.toLowerCase().includes(q) ||
        s.nome.toLowerCase().includes(q) ||
        s.cnpj.includes(q)
      );
    });
  }, [sellersInPlano, sellerQuery]);

  function closeSellersModal() {
    setSellersModalOpen(false);
    setSellerQuery('');
    setSellerToAdd('');
    setSellerSearchExpanded(false);
  }
  function addSeller(id: string) {
    if (!id || sellersInPlano.includes(id)) return;
    setSellersInPlano((prev) => [...prev, id]);
    setSellerToAdd('');
  }
  function removeSeller(id: string) {
    setSellersInPlano((prev) => prev.filter((x) => x !== id));
  }

  const grupoAtual = useMemo(
    () => RATES_MOCK.find((g) => g.groupId === grupoId) ?? RATES_MOCK[0],
    [grupoId],
  );

  const creditoMatrix =
    tipoPagamento === 'credito-online'
      ? grupoAtual.credito.online
      : grupoAtual.credito.pos;

  function handleBack() {
    navigate('/backoffice/taxas-company/copia-cola');
  }

  return (
    <>
    <BackofficeLayout activeItem="taxas-company-copia-cola">
      <main className="content">
        <div className="content-inner dpt-page">
          <div className="dpt-page-header-wrap" ref={headerWrapRef}>
            <PageHeading
              title={planoNome}
              onBack={editMode ? undefined : handleBack}
              backButtonLabel="Voltar"
              secondaryActionLabel={editMode ? 'Cancelar' : undefined}
              onSecondaryAction={editMode ? cancelEdit : undefined}
              primaryActionLabel={editMode ? 'Salvar' : undefined}
              onPrimaryAction={editMode ? saveEdit : undefined}
            />
            {!editMode && (
              <div className="dpt-editar-btn-wrap">
                <Button
                  variant="outline"
                  size="base"
                  onClick={() => setHistoricoModalOpen(true)}
                >
                  Ver histórico
                </Button>
                <Button
                  variant="fill"
                  size="base"
                  iconRight={<Icon icon={CaretDown} size={16} weight="bold" />}
                  onClick={() => setEditDropdownOpen((o) => !o)}
                >
                  Editar
                </Button>
              </div>
            )}
            {editDropdownOpen && (
              <div className="dpt-edit-dropdown">
                <DrawerList>
                  <ActionDrawerItems
                    items={[
                      {
                        label: 'Edição individual',
                        variant: 'icon',
                        icon: <Icon icon={PencilSimple} size={16} weight="regular" />,
                        onClick: () => { setEditDropdownOpen(false); startEdit(); },
                      },
                      {
                        label: 'Edição em massa',
                        variant: 'icon',
                        icon: <Icon icon={UploadSimple} size={16} weight="regular" />,
                        onClick: () => {
                          setEditDropdownOpen(false);
                          navigate(`/backoffice/taxas-company/plano/${planoId}/edicao-massa`);
                        },
                      },
                    ]}
                  />
                </DrawerList>
              </div>
            )}
          </div>

          <div className="dpt-subheader">
            <Pills label={COMPANY_MOCK.nome} state="default" variant="light" size="sm" />
            <Pills
              label={`${RATES_MOCK.length} grupos de MCC`}
              state="default"
              variant="light"
              size="sm"
            />
            <button
              type="button"
              className="dpt-pill-btn"
              onClick={() => setSellersModalOpen(true)}
              aria-label="Abrir gestão de sellers vinculados"
            >
              <Pills
                label={`${sellersCount} sellers vinculados`}
                state="info"
                variant="light"
                size="sm"
                icon={Storefront}
              />
            </button>
          </div>

          {/* ── 5 cards de custos base do plano ───────────────────────────── */}
          <div className="dpt-mini-cards-grid">
            {([
              { key: 'cdi',        label: '% CDI' },
              { key: 'gateway',    label: 'Processamento' },
              { key: 'antifraude', label: 'Antifraude' },
              { key: 'repasse',    label: 'Repasse' },
              { key: 'tds',        label: '3DS' },
            ] as const).map((card) => (
              <div key={card.key} className="dpt-mini-card">
                <Text variant="caption-sm" className="dpt-mini-card-title">{card.label}</Text>
                {editMode && editValues ? (
                  <input
                    className="dpt-edit-input"
                    value={editValues.miniCards[card.key]}
                    onChange={(e) =>
                      setEditValues({
                        ...editValues,
                        miniCards: { ...editValues.miniCards, [card.key]: e.target.value },
                      })
                    }
                  />
                ) : (
                  <Text variant="label-base" className="dpt-mini-card-value">
                    {card.key === 'cdi' ? '130%' :
                     card.key === 'tds' ? '0,59%' :
                     card.key === 'repasse' ? 'D+1' : 'R$ 0,29'}
                  </Text>
                )}
              </div>
            ))}
          </div>

          {/* ── 3 cards de método ────────────────────────────────────────── */}
          <div className="dpt-cards-grid">
            {(['pix', 'boleto', 'debito'] as const).map((m) => {
              const titulo = m === 'pix' ? 'PIX' : m === 'boleto' ? 'Boleto' : 'Débito';
              const base = PLANO_TAXAS_METODO[m];
              const baseTipoLabel = base.tipo === '%' ? 'Porcentagem' : 'Fixo';
              const renderRow = (label: string, field: 'tempo' | 'tipo' | 'taxa' | 'limite', staticValue: string) => (
                <Section.Row
                  label={label}
                  content={
                    editMode && editValues ? (
                      <input
                        className="dpt-edit-input dpt-edit-input--inline"
                        value={editValues[m][field]}
                        onChange={(e) => setEditValues({
                          ...editValues,
                          [m]: { ...editValues[m], [field]: e.target.value },
                        })}
                      />
                    ) : (
                      <Text variant="label-sm">{staticValue}</Text>
                    )
                  }
                />
              );
              return (
                <div key={m} className="dpt-card">
                  <Text variant="label-sm" className="dpt-card-title">{titulo}</Text>
                  <Section title="" className="dpt-section-headless">
                    {renderRow('Tempo de repasse', 'tempo', base.tempo)}
                    {renderRow('Tipo de taxa', 'tipo', baseTipoLabel)}
                    {renderRow('Taxa', 'taxa', base.taxa)}
                    {renderRow('Limite de taxas', 'limite', m === 'debito' ? '—' : base.limite)}
                  </Section>
                </div>
              );
            })}
          </div>

          {/* ── Filtros de crédito ───────────────────────────────────────── */}
          <div className="dpt-filters">
            <div className="dpt-filters-left">
              <Text variant="label-sm" className="dpt-card-title">Cartões de Crédito</Text>
              <div className="dpt-mcc-pills-row">
                <Pills label="Online" state="warning" variant="light" size="sm" />
                <Pills label="POS" state="warning" variant="light" size="sm" />
                {grupoAtual.mccs.map((code) => (
                  <Pills
                    key={code}
                    label={code}
                    state="accent"
                    variant="light"
                    size="sm"
                  />
                ))}
              </div>
            </div>
            <div className="dpt-filters-right">
              <div className="dpt-filter-select">
                <GroupSelect
                  value={grupoId}
                  onChange={setGrupoId}
                  options={RATES_MOCK.map((g, idx) => ({
                    value: g.groupId,
                    label: `MCCs - Grupo ${idx + 1}`,
                    isDefault: g.groupId === defaultGroupId,
                  }))}
                  onSetDefault={editMode ? (id) => setConfirmDefaultGroupId(id) : undefined}
                />
              </div>
              {editMode && (
                <Button
                  variant="outline"
                  size="base"
                  iconLeft={<Icon icon={Plus} size={16} weight="bold" />}
                  onClick={() => {}}
                  aria-label="Adicionar grupo de MCC"
                >
                  Grupo
                </Button>
              )}
            </div>
          </div>

          {/* ── Spreadsheet de bandeiras × parcelas ──────────────────────── */}
          <div className="dpt-grid-wrapper">
            <div
              className="dpt-grid"
              style={{ gridTemplateColumns: `120px repeat(${BANDEIRAS.length}, 1fr)` }}
            >
              {/* Corner */}
              <div className="dpt-grid-corner">
                <Text variant="caption-md">Parcela</Text>
              </div>
              {BANDEIRAS.map((b) => (
                <div key={b} className="dpt-grid-col-header">
                  <Text variant="label-sm">{b}</Text>
                </div>
              ))}

              {/* Rows */}
              {PARCELAS.map((p, rIdx) => (
                <React.Fragment key={p}>
                  <div className="dpt-grid-row-header">
                    <Text variant="label-sm">{p}</Text>
                  </div>
                  {BANDEIRAS.map((b, cIdx) => {
                    const val = creditoMatrix[rIdx]?.[cIdx];
                    if (editMode && editValues) {
                      return (
                        <div key={`${rIdx}-${cIdx}`} className="dpt-grid-cell dpt-grid-cell--edit">
                          <input
                            className="dpt-edit-input dpt-edit-input--cell"
                            value={editValues.matrix[rIdx]?.[cIdx] ?? ''}
                            onChange={(e) => {
                              const next = editValues.matrix.map((row) => row.slice());
                              if (!next[rIdx]) next[rIdx] = ['', '', '', '', ''];
                              next[rIdx][cIdx] = e.target.value;
                              setEditValues({ ...editValues, matrix: next });
                            }}
                          />
                        </div>
                      );
                    }
                    return (
                      <button
                        key={`${rIdx}-${cIdx}`}
                        type="button"
                        className="dpt-grid-cell dpt-grid-cell-btn"
                        onClick={() => {
                          setDrawerParcelaIdx(rIdx);
                          setDrawerBandeiraIdx(cIdx);
                          setCellDrawerOpen(true);
                        }}
                        aria-label={`Ver taxas calculadas de ${p} ${b}`}
                      >
                        <Text variant="body-sm">
                          {val != null ? `${val.toFixed(2).replace('.', ',')}%` : '—'}
                        </Text>
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>

        </div>
      </main>
    </BackofficeLayout>

      {/* ── Drawer: taxas calculadas por dia de repasse ─────────────────── */}
      <Drawer
        isOpen={cellDrawerOpen}
        title="Taxas calculadas"
        onClose={() => setCellDrawerOpen(false)}
        footer={{
          primaryButton: {
            children: 'Fechar',
            onClick: () => setCellDrawerOpen(false),
          },
        }}
      >
        <div className="dpt-cell-drawer-body">
          <div className="dpt-cell-drawer-context">
            <Pills label={grupoAtual.groupLabel} state="default" variant="light" size="sm" />
            <Pills label={tipoPagamento === 'credito-online' ? 'Online' : 'POS'} state="accent" variant="light" size="sm" />
          </div>

          <div className="dpt-cell-drawer-filters">
            <Select
              label="Parcela"
              value={String(drawerParcelaIdx)}
              onChange={(v) => setDrawerParcelaIdx(parseInt(v, 10))}
              options={PARCELAS.map((p, idx) => ({ value: String(idx), label: p }))}
            />
            <Select
              label="Bandeira"
              value={String(drawerBandeiraIdx)}
              onChange={(v) => setDrawerBandeiraIdx(parseInt(v, 10))}
              options={BANDEIRAS.map((b, idx) => ({ value: String(idx), label: b }))}
            />
          </div>

          {(() => {
            const baseRate = creditoMatrix[drawerParcelaIdx]?.[drawerBandeiraIdx] ?? 0;
            type Row = { dia: string; antecipacao: number; total: number };
            const rows: Row[] = REPASSE_DAYS.map((d) => {
              const ant = ANTECIPACAO_DELTA[d] ?? 0;
              return { dia: d, antecipacao: ant, total: baseRate + ant };
            });
            const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')}%`;
            const cols: ColumnDefinition<Row>[] = [
              {
                key: 'dia',
                title: 'Dia de repasse',
                dataIndex: 'dia',
                width: 140,
                render: (v) => <Text variant="label-sm">{v as string}</Text>,
              },
              {
                key: 'antecipacao',
                title: 'Taxa de antecipação',
                dataIndex: 'antecipacao',
                render: (v) => <Text variant="body-sm">{fmt(v as number)}</Text>,
              },
              {
                key: 'total',
                title: 'Taxa total',
                dataIndex: 'total',
                render: (v) => <Text variant="label-sm">{fmt(v as number)}</Text>,
              },
            ];
            return (
              <div className="dpt-cell-drawer-table">
                <Table<Row>
                  data={rows}
                  columns={cols}
                  rowKey={(r) => r.dia}
                />
              </div>
            );
          })()}
        </div>
      </Drawer>

      {/* ── Modal: gestão de sellers vinculados ────────────────────────── */}
      <Modal
        isOpen={sellersModalOpen}
        title={`Sellers vinculados ao plano "${planoNome}"`}
        onClose={closeSellersModal}
        size="lg"
        footer={{
          primaryButton: {
            children: 'Fechar',
            onClick: closeSellersModal,
          },
        }}
      >
        <div className="dpt-sellers-modal">
          <div className="dpt-sellers-modal-add">
            <Select
              placeholder="Adicionar seller ao plano"
              value={sellerToAdd}
              onChange={(v) => setSellerToAdd(v)}
              searchable
              options={modalAvailableSellers.map((s) => ({
                value: s.id,
                label: `${s.id} — ${s.nome}`,
              }))}
            />
            <Button
              variant="fill"
              size="base"
              disabled={!sellerToAdd}
              onClick={() => addSeller(sellerToAdd)}
            >
              Adicionar
            </Button>
          </div>

          <div className="dpt-sellers-modal-summary">
            <Pills
              label={`${sellersCount} seller${sellersCount !== 1 ? 's' : ''} no plano`}
              state="info"
              variant="light"
              size="sm"
              icon={Storefront}
            />
            {sellerQuery && (
              <Text variant="caption-md" className="dpt-sellers-modal-filter">
                {modalFilteredSellers.length} resultado{modalFilteredSellers.length !== 1 ? 's' : ''} para "{sellerQuery}"
              </Text>
            )}
            <div className="dpt-sellers-modal-search">
              <Search
                expanded={sellerSearchExpanded}
                value={sellerQuery}
                onChange={setSellerQuery}
                onExpand={(expanded) => {
                  setSellerSearchExpanded(expanded);
                  if (!expanded) setSellerQuery('');
                }}
                placeholder="Buscar por ID, nome ou CNPJ"
              />
            </div>
          </div>

          <div className="dpt-sellers-modal-list">
            {modalFilteredSellers.length === 0 && (
              <div className="dpt-sellers-modal-empty">
                <Text variant="body-sm">
                  {sellerQuery
                    ? 'Nenhum seller encontrado com esse filtro.'
                    : 'Nenhum seller vinculado a este plano. Use o campo acima para adicionar.'}
                </Text>
              </div>
            )}
            {modalFilteredSellers.map((id) => {
              const s = ALL_SELLERS.find((x) => x.id === id);
              if (!s) return null;
              return (
                <div key={id} className="dpt-sellers-modal-row">
                  <div className="dpt-sellers-modal-row-info">
                    <Text variant="label-sm">{s.nome}</Text>
                    <Text variant="caption-md" className="dpt-sellers-modal-row-meta">
                      {s.id} · {s.cnpj}
                    </Text>
                  </div>
                  <button
                    type="button"
                    className="dpt-sellers-modal-row-remove"
                    onClick={() => removeSeller(id)}
                    aria-label={`Remover ${s.nome}`}
                  >
                    <Icon icon={Trash} size={16} weight="regular" />
                    Remover
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* ── Modal: confirmar troca do grupo default ──────────────────── */}
      <Modal
        isOpen={confirmDefaultGroupId !== null}
        title="Trocar grupo default"
        onClose={() => setConfirmDefaultGroupId(null)}
        size="sm"
        footer={{
          primaryButton: {
            children: 'Confirmar',
            onClick: () => {
              if (confirmDefaultGroupId) setDefaultGroupId(confirmDefaultGroupId);
              setConfirmDefaultGroupId(null);
            },
          },
          secondaryButton: {
            children: 'Cancelar',
            variant: 'outline',
            onClick: () => setConfirmDefaultGroupId(null),
          },
        }}
      >
        <Text variant="body-sm">
          Confirma mudar o plano default de <strong>{labelGrupo(defaultGroupId)}</strong> para{' '}
          <strong>{confirmDefaultGroupId ? labelGrupo(confirmDefaultGroupId) : ''}</strong>?
        </Text>
      </Modal>

      {/* ── Modal: histórico de alterações (placeholder) ───────────────── */}
      <Modal
        isOpen={historicoModalOpen}
        title="Histórico de alterações"
        onClose={() => setHistoricoModalOpen(false)}
        size="lg"
        footer={{
          primaryButton: {
            children: 'Fechar',
            onClick: () => setHistoricoModalOpen(false),
          },
        }}
      >
        <div className="dpt-historico-placeholder">
          <Icon icon={ClockCounterClockwise} size={32} weight="regular" />
          <Text variant="body-base">
            O histórico de alterações deste plano será especificado em entrega futura.
          </Text>
          <Text variant="caption-md">
            Mostrará data, responsável e o que mudou em cada edição (taxas, MCCs, grupos).
          </Text>
        </div>
      </Modal>
    </>
  );
}
